import { useState } from 'react';
import { Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import MentorshipService, {
    MentorshipCampusOption,
    MentorshipMatchingPair,
} from './MentorshipService';

/** Ranked shortlist size per mentee — matches the matching prompt and LLM JSON schema. */
const MAX_MENTOR_OPTIONS = 3;

interface MentorshipMatchingMenteeCardProps {
    options: MentorshipMatchingPair[];
    campuses?: MentorshipCampusOption[];
    disabled?: boolean;
    busy?: boolean;
    onSelect: (pairId: number) => void;
    onConfirm: (pairId: number) => void;
}

const otherMatchLine = (row: MentorshipMatchingPair['otherMenteeMatchDetails'][number]): string => {
    const name = row.name?.trim() || 'Mentee';
    const programBits = [row.programName, row.batchYear != null ? String(row.batchYear) : null]
        .map((value) => (value == null ? '' : String(value).trim()))
        .filter(Boolean);
    const status = MentorshipService.displayMatchingAssignmentStatus(row.assignmentStatus);
    const selected = row.isSelected ? 'selected' : 'not selected';
    const rank = row.rank == null ? '—' : String(row.rank);
    const extra = [programBits.join(' · '), `rank ${rank}`, status, selected].filter(Boolean);
    return extra.length > 0 ? `${name} — ${extra.join(' · ')}` : name;
};

const otherMatchesTooltipBody = (option: MentorshipMatchingPair) => {
    const details = option.otherMenteeMatchDetails || [];
    if (details.length === 0) {
        return option.otherMenteeMatches > 0
            ? `${option.otherMenteeMatches} other mentee${option.otherMenteeMatches === 1 ? '' : 's'} also matched with this mentor.`
            : 'No other mentees currently matched with this mentor.';
    }
    return (
        <div className="text-start" style={{ maxHeight: 240, overflowY: 'auto' }}>
            <div className="fw-semibold mb-1">Other mentees matched with this mentor</div>
            {details.map((row, index) => (
                <div key={row.menteeId ?? `${row.name || 'mentee'}-${index}`} className="mb-1">
                    {otherMatchLine(row)}
                    {row.email ? <div className="opacity-75">{row.email}</div> : null}
                </div>
            ))}
        </div>
    );
};

const optionWouldBeRejected = (option: MentorshipMatchingPair, options: MentorshipMatchingPair[]): boolean => {
    if (options.some((row) => row.assignmentStatus === 'Published')) return true;
    // Declined mentors stay visible as a theoretical match but cannot be assigned until they
    // re-confirm, so they are also excluded from Switch/Change Mentee Assignment choices.
    if (MentorshipService.isMentorPoolDeclined(option.mentor.confirmationStatus)) return true;
    if (option.isSelected) return false;
    const noCapacity = option.remainingCapacity == null || option.remainingCapacity <= 0;
    const confirmed = options.find((row) => row.assignmentStatus === 'Confirmed');
    if (confirmed) {
        const confirmedRank = confirmed.rank ?? 0;
        const targetRank = option.rank ?? 0;
        if (targetRank <= confirmedRank) return true;
        return noCapacity;
    }
    return noCapacity;
};

const MentorshipMatchingMenteeCard = ({
    options,
    campuses = [],
    disabled = false,
    busy = false,
    onSelect,
    onConfirm,
}: MentorshipMatchingMenteeCardProps) => {
    const [showReassign, setShowReassign] = useState(false);
    const [draftPairId, setDraftPairId] = useState<number | null>(null);

    const ranked = [...options]
        .filter((row) => row.id != null)
        .sort((a, b) => (a.rank ?? 0) - (b.rank ?? 0))
        .slice(0, MAX_MENTOR_OPTIONS);
    const mentee = ranked[0]?.mentee;
    const selected = ranked.find((row) => row.isSelected) || ranked[0];
    const menteeStatus = MentorshipService.displayMatchingAssignmentStatus(selected?.assignmentStatus);
    const photo = MentorshipService.profilePictureSrc(mentee?.profilePicture);
    // Student List profile link (§Intake-14 pattern). Falls back to plain text when the pair row
    // carries no uuid or name, rather than rendering a dead link.
    const menteeName = mentee?.name || '—';
    const menteeUuid = mentee?.uuid == null ? '' : String(mentee.uuid).trim();
    const menteeProfileHref = menteeUuid && mentee?.name ? `/student/${menteeUuid}/profile` : null;
    const yearLabel =
        (mentee?.eligibleYears || []).map((year) => MentorshipService.formatMenteePoolYearLabel(year)).join(', ') || '—';
    const menteeTracks =
        (mentee?.tracks || [])
            .map((track) => track.name)
            .filter(Boolean)
            .join(', ') || '—';
    const canConfirm =
        Boolean(selected) && selected?.assignmentStatus === 'Pending Decision' && Boolean(selected?.isSelected);
    const reassignChoices = ranked.filter((row) => !row.isSelected && !optionWouldBeRejected(row, ranked));

    const openReassign = () => {
        setDraftPairId(reassignChoices[0]?.id ?? null);
        setShowReassign(true);
    };

    const closeReassign = () => {
        setShowReassign(false);
        setDraftPairId(null);
    };

    const saveReassign = () => {
        if (draftPairId == null) return;
        onSelect(draftPairId);
        closeReassign();
    };

    if (!mentee) return null;

    return (
        <div className="card border-0 rounded-3 mb-4 shadow-sm bg-white overflow-hidden">
            <div className="card-header border-bottom bg-light px-4 py-3 d-flex flex-wrap align-items-center justify-content-between gap-2">
                <div className="d-flex align-items-center gap-3">
                    {photo ? (
                        <img
                            src={photo}
                            alt=""
                            width={36}
                            height={36}
                            className="rounded-circle"
                            style={{ objectFit: 'cover' }}
                        />
                    ) : (
                        <div
                            className="rounded-circle bg-white d-flex align-items-center justify-content-center shadow-sm"
                            style={{ width: 36, height: 36, fontSize: 13, fontWeight: 600 }}>
                            {(mentee.name || '?').slice(0, 1).toUpperCase()}
                        </div>
                    )}
                    <div>
                        <div className="font-14 font-700 text-dark">
                            {menteeProfileHref ? (
                                <a
                                    className="font-14 font-700 text-dark text-decoration-none"
                                    href={menteeProfileHref}
                                    target="_blank"
                                    rel="noopener noreferrer">
                                    {menteeName}
                                </a>
                            ) : (
                                menteeName
                            )}
                        </div>
                        <div className="font-12 text-secondary">
                            {mentee.programName || '—'} · {yearLabel} · Career tracks: {menteeTracks} · Mentorship
                            goals: — ·{' '}
                        </div>
                    </div>
                    <div className="d-flex gap-1 ms-2">
                        <span className="badge bg-primary bg-opacity-10 text-primary-emphasis font-11 font-500 px-2 py-1 rounded-pill">
                            Selected mentor: {selected?.mentor.name || '—'}
                            {selected?.rank === 1 &&
                            selected.isSelected &&
                            selected.assignmentStatus === 'Pending Decision'
                                ? ' (default selected, not final)'
                                : ''}
                        </span>
                        <span className="badge bg-primary bg-opacity-10 text-primary-emphasis font-11 font-500 px-2 py-1 rounded-pill">
                            Matching status: {menteeStatus}
                        </span>
                    </div>
                </div>
                <div className="d-flex align-items-center gap-2">
                    <span
                        className="badge bg-success-subtle text-success-emphasis rounded-pill d-inline-flex align-items-center font-12 fw-bold px-2.5 py-1"
                        id="matching-status-arjun">
                        <svg width="6" height="6" className="me-1">
                            <circle cx="3" cy="3" r="3" fill="#10B981"></circle>
                        </svg>{' '}
                        Mentor Selected
                    </span>
                    <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm font-11 font-600 py-1 px-2.5 rounded-pill"
                        disabled={disabled || busy}
                        onClick={openReassign}>
                        ✎ Change Mentee Assignment
                    </button>
                    <button
                        type="button"
                        className="btn btn-success text-white btn-sm font-11 font-700 py-1 px-2.5 rounded-pill"
                        disabled={disabled || busy || !canConfirm}
                        onClick={() => selected && onConfirm(selected.id)}>
                        ✓ Confirm Pair
                    </button>
                </div>
            </div>
            <div className="px-4 py-2 bg-primary bg-opacity-10 text-primary border-bottom font-12 font-500">
                🎯 <strong>Goal:</strong> Management consulting career, case interview preparation, and targeting MBB
                firms in placement season
            </div>
            <div className="card-body p-4">
                <div className="row g-3">
                    {ranked.map((option) => {
                    const rejected = optionWouldBeRejected(option, ranked);
                    const mentorTracks =
                        (option.mentor.tracks || [])
                            .map((track) => track.name)
                            .filter(Boolean)
                            .join(', ') || '—';
                    const mentorName = option.mentor.name || '—';
                    const mentorUuid = option.mentor.uuid == null ? '' : String(option.mentor.uuid).trim();
                    const mentorProfileHref =
                        mentorUuid && option.mentor.name ? `/alumni/${mentorUuid}/profile` : null;
                    // A mentor can decline after the run generated these pairs, so the pair row
                    // survives with a Declined pool status (DEC-P5-020 only gates the run universe).
                    const mentorDeclined = MentorshipService.isMentorPoolDeclined(option.mentor.confirmationStatus);
                    // Prototype copy leads with the mentor's first name; fall back to a neutral
                    // subject rather than inventing one when the snapshot has no name.
                    const mentorFirstName = option.mentor.name
                        ? String(option.mentor.name).trim().split(' ').filter(Boolean)[0]
                        : 'This mentor';
                    return (
                        <div className={`col-md-${ranked.length > 2 ? 4 : 6}`}>
                            <div
                                key={option.id}
                                style={mentorDeclined ? { opacity: 0.85 } : undefined}
                                className={`card rounded-3 shadow-sm transition-all h-100 ${
                                    mentorDeclined
                                        ? 'border border-light-subtle bg-danger-subtle'
                                        : `border border-success border-opacity-10 ${option.isSelected ? 'bg-success bg-opacity-10' : ''}`
                                }`}>
                                    <div className="card-header bg-transparent border-bottom border-opacity-10">
                                        <div className="d-flex flex-wrap align-items-center gap-3">
                                            {/* <svg width="22" height="22" className="rounded-circle">
                                                <circle cx="11" cy="11" r="11" fill={mentorDeclined ? '#6c757d' : '#198754'}></circle>
                                                <text
                                                    x="50%"
                                                    y="55%"
                                                    font-family="Outfit"
                                                    font-size="11"
                                                    font-weight="bold"
                                                    fill="#ffffff"
                                                    dominant-baseline="middle"
                                                    text-anchor="middle">
                                                    {option.rank ?? '—'}
                                                </text>
                                            </svg> */}
                                            <svg
                                                width="40"
                                                height="40"
                                                className={`rounded-circle shadow-sm ${option.isSelected && !mentorDeclined ? 'bg-white' : ''}`}>
                                                <rect
                                                    width="100%"
                                                    height="100%"
                                                    fill={mentorDeclined ? '#FEE2E2' : option.isSelected ? '#fff' : '#EBF0FF'}></rect>
                                                <text
                                                    x="50%"
                                                    y="55%"
                                                    font-family="Outfit"
                                                    font-size="14"
                                                    font-weight="bold"
                                                    fill={mentorDeclined ? '#DC2626' : '#3557CC'}
                                                    dominant-baseline="middle"
                                                    text-anchor="middle">
                                                    {option.mentor.name
                                                        ? option.mentor.name
                                                            .split(' ')
                                                            .filter(Boolean)
                                                            .map((word) => word[0].toUpperCase())
                                                            .join('')
                                                        : '—'}
                                                </text>
                                            </svg>
                                            <div className="flex-grow-1">
                                                <div className="font-14 font-700 text-dark">
                                                    {mentorProfileHref ? (
                                                        <a
                                                            className="font-14 font-700 text-dark text-decoration-none"
                                                            href={mentorProfileHref}
                                                            target="_blank"
                                                            rel="noopener noreferrer">
                                                            {mentorName}
                                                        </a>
                                                    ) : (
                                                        mentorName
                                                    )}
                                                </div>
                                                <div className="font-12 text-secondary">
                                                    {[option.mentor.designation, option.mentor.company].filter(Boolean).join(' · ') || '—'}
                                                    {mentorDeclined ? (
                                                        <span className="text-danger font-11 fw-bold"> (Declined this cohort)</span>
                                                    ) : null}
                                                </div>
                                                <div className="font-11 text-muted">
                                                    {[option.mentor.programName, option.mentor.batchYear != null ? String(option.mentor.batchYear) : null]
                                                        .filter(Boolean)
                                                        .join(' · ') || '—'}
                                                </div>
                                            </div>
                                            <OverlayTrigger
                                                placement="left"
                                                overlay={
                                                    <Tooltip
                                                        id={`mentorship-other-matches-${option.id}`}
                                                        style={{ maxWidth: 360, textAlign: 'left' }}>
                                                        {otherMatchesTooltipBody(option)}
                                                    </Tooltip>
                                                }>
                                                <div
                                                    className="text-end"
                                                    role="note"
                                                    tabIndex={0}
                                                    style={{ cursor: 'help' }}
                                                    aria-label={`${option.otherMenteeMatches} other matches`}>
                                                    <div className="font-18 font-700  text-success lh-1">
                                                        {option.otherMenteeMatches}
                                                    </div>
                                                    <div className="font-10 text-uppercase font-600 text-muted">
                                                        other matches
                                                    </div>
                                                </div>
                                            </OverlayTrigger>
                                        </div>
                                    </div>
                                    <div className="card-body">
                                        <div className="row g-3 mb-3">
                                            <div className="col-md-12">
                                                <div className="font-11 font-700 text-uppercase tracking-wider text-muted mb-1">
                                                    Tracks
                                                </div>
                                                <div className="d-flex gap-1 flex-wrap">
                                                    <span className={`badge ${option.isSelected ? 'bg-white' : 'bg-secondary-subtle'} text-secondary font-11 font-500 rounded-pill px-2 py-1`}>
                                                        {mentorTracks}
                                                    </span>
                                                    <span className={`badge ${option.isSelected ? 'bg-white' : 'bg-secondary-subtle'} text-secondary font-11 font-500 rounded-pill px-2 py-1`}>
                                                        Strategy
                                                    </span>
                                                    <span className={`badge ${option.isSelected ? 'bg-white' : 'bg-secondary-subtle'} text-secondary font-11 font-500 rounded-pill px-2 py-1`}>
                                                        Market Expansion
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="col-md-12 ">
                                                {mentorDeclined ? (
                                                    <div className="border-start border-danger border-3 p-3 bg-white rounded-3 text-danger-emphasis">
                                                        ⚠ {mentorFirstName} has <em>declined participation for this cohort</em>.
                                                        They remain in the system and are shown as a theoretical match — but cannot
                                                        be assigned unless they re-confirm.
                                                    </div>
                                                ) : (
                                                    <div className={`border-start border-3 p-3  rounded-3 font-14 ${option.isSelected ? 'bg-white border-success' : 'border-secondary bg-secondary bg-opacity-10 text-secondary-emphasis'}`}>
                                                        {option.reasoning == null || String(option.reasoning).trim() === ''
                                                            ? '—'
                                                            : MentorshipService.formatMatchingReasoningWithCampusNames(
                                                                  option.reasoning,
                                                                  campuses
                                                              )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="card-footer bg-transparent border-top border-opacity-10 pt-3">
                                                <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                                                    <div className="d-flex align-items-center gap-2">
                                                        {mentorDeclined ? (
                                                            <span className="badge bg-danger-subtle text-danger-emphasis rounded-pill px-2.5 py-1 font-11">
                                                                Declined — This Cohort
                                                            </span>
                                                        ) : (
                                                            <>
                                                                <span
                                                                    className={`badge  rounded-pill px-2.5 py-1 font-11 font-500 ${option.isSelected ? 'bg-success' : 'bg-secondary'}`}
                                                                    id="mo-chip-arjun-1">
                                                                    {option.isSelected ? 'selected' : 'Not selected'}
                                                                </span>
                                                                <span className="font-11 text-secondary">1 mentee assigned (max 2)</span>
                                                            </>
                                                        )}
                                                    </div>
                                                    <div className="d-flex gap-2">
                                                        {mentorDeclined ? (
                                                            <button
                                                                type="button"
                                                                className="btn btn-outline-secondary btn-xs font-11 font-600 py-1 px-2.5 rounded-2"
                                                                disabled>
                                                                Cannot Assign
                                                            </button>
                                                        ) : option.isSelected ? (
                                                            <div className="d-flex flex-wrap gap-2">
                                                                {ranked
                                                                    .filter((row) => !row.isSelected)
                                                                    .map((row) => (
                                                                        <button
                                                                            key={row.id}
                                                                            type="button"
                                                                            className="btn btn-outline-secondary btn-xs font-11 font-600 py-1 px-2.5 rounded-2"
                                                                            disabled={disabled || busy || optionWouldBeRejected(row, ranked)}
                                                                            onClick={() => onSelect(row.id)}>
                                                                            Switch to Option {row.rank ?? row.id}
                                                                        </button>
                                                                    ))}
                                                            </div>
                                                        ) : (
                                                            <button
                                                                type="button"
                                                                className="btn btn-outline-secondary btn-xs font-11 font-600 py-1 px-2.5 rounded-2"
                                                                disabled={disabled || busy || rejected}
                                                                onClick={() => onSelect(option.id)}>
                                                                Select this mentor
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>

                                                <div className="d-flex flex-wrap justify-content-between gap-2 mb-2">
                                                    <div className="font-600">
                                                        {option.rank === 1 &&
                                                        option.isSelected &&
                                                        option.assignmentStatus === 'Pending Decision'
                                                            ? ' · default selected'
                                                            : ''}
                                                    </div>
                                                </div>
                                    </div>
                            </div>
                        </div>
                    );
                })}
                </div>
                
            </div>

            <Modal show={showReassign} onHide={closeReassign}>
                <Modal.Header closeButton>
                    <Modal.Title>Change Mentee Assignment</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="font-13 text-secondary mb-3">
                        Select a mentor option for {mentee.name || 'this mentee'}. After a confirmed pair, only
                        lower-ranked mentors with remaining capacity are available.
                    </p>
                    {reassignChoices.length === 0 ? (
                        <div className="text-muted">No eligible mentor options available to reassign.</div>
                    ) : (
                        <div className="list-group">
                            {reassignChoices.map((row) => (
                                <label
                                    key={row.id}
                                    className={`list-group-item list-group-item-action d-flex gap-2 align-items-start ${
                                        draftPairId === row.id ? 'active' : ''
                                    }`}>
                                    <input
                                        type="radio"
                                        className="form-check-input mt-1"
                                        name={`reassign-mentee-${mentee.id ?? 'card'}`}
                                        checked={draftPairId === row.id}
                                        onChange={() => setDraftPairId(row.id)}
                                    />
                                    <span>
                                        <span className="font-600">
                                            Option {row.rank ?? '—'}: {row.mentor.name || '—'}
                                        </span>
                                        <div className={draftPairId === row.id ? 'font-12' : 'font-12 text-secondary'}>
                                            {row.mentor.company || '—'} · Remaining capacity:{' '}
                                            {row.remainingCapacity == null ? '—' : row.remainingCapacity}
                                        </div>
                                    </span>
                                </label>
                            ))}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <button type="button" className="btn btn-outline-secondary" onClick={closeReassign}>
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="btn btn-success"
                        disabled={disabled || busy || draftPairId == null}
                        onClick={saveReassign}>
                        Save Assignment
                    </button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default MentorshipMatchingMenteeCard;
